import React, { createContext, useContext, useEffect, useState } from "react";
import { ModeKey } from "../mode/modes";

type GlobalSettings = {
  currency: string;
  equalizeLength: boolean;
};

type SettingKey = keyof GlobalSettings;

type ControlledSettings = {
  [K in SettingKey]?: {
    controlledBy: ModeKey;
    override: GlobalSettings[K];
  };
};

type GlobalSettingsContextValue = {
  settings: GlobalSettings;
  setSettings: React.Dispatch<React.SetStateAction<GlobalSettings>>;
  effectiveSettings: GlobalSettings;
  controlledSettings: ControlledSettings;
  setModeControl: (
    modeKey: ModeKey | null,
    overrides?: Partial<GlobalSettings>,
  ) => void;
  isSettingControlled: (setting: SettingKey) => boolean;
};

const predictCurrency = (language: string): string => {
  switch (language) {
    case "en-GB":
      return "GBP";
    case "en-US":
      return "USD";
    default:
      return "GBP";
  }
};

const GlobalSettingsContext = createContext<GlobalSettingsContextValue | null>(
  null,
);

export const GlobalSettingsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [settings, setSettings] = useState<GlobalSettings>(() => {
    const stored = localStorage.getItem("globalSettings");
    return stored
      ? JSON.parse(stored)
      : {
          currency: predictCurrency(navigator.language),
          equalizeLength: false,
        };
  });

  const [controlledSettings, setControlledSettings] =
    useState<ControlledSettings>({});

  useEffect(() => {
    localStorage.setItem("globalSettings", JSON.stringify(settings));
  }, [settings]);

  // Calculate effective settings (base settings + mode overrides)
  const effectiveSettings: GlobalSettings = {
    ...settings,
    ...Object.entries(controlledSettings).reduce((acc, [key, value]) => {
      if (value) {
        (acc as any)[key] = value.override;
      }
      return acc;
    }, {} as Partial<GlobalSettings>),
  };

  const setModeControl = React.useCallback(
    (modeKey: ModeKey | null, overrides?: Partial<GlobalSettings>) => {
      if (!modeKey || !overrides) {
        // Clear all mode controls when no mode or no overrides
        setControlledSettings({});
        return;
      }

      // Set mode controls with overrides
      setControlledSettings((prev) => {
        const next = { ...prev };
        // First clear any existing controls
        Object.keys(next).forEach((key) => {
          delete next[key as SettingKey];
        });
        // Then set the new controls
        Object.entries(overrides).forEach(([key, value]) => {
          (next as any)[key] = {
            controlledBy: modeKey,
            override: value as any,
          };
        });
        return next;
      });
    },
    [],
  );

  const isSettingControlled = React.useCallback(
    (setting: SettingKey): boolean => {
      return !!controlledSettings[setting];
    },
    [controlledSettings],
  );

  return (
    <GlobalSettingsContext.Provider
      value={{
        settings,
        setSettings,
        effectiveSettings,
        controlledSettings,
        setModeControl,
        isSettingControlled,
      }}
    >
      {children}
    </GlobalSettingsContext.Provider>
  );
};

export const useGlobalSettings = () => {
  const ctx = useContext(GlobalSettingsContext);
  if (!ctx)
    throw new Error(
      "useGlobalSettings must be used inside GlobalSettingsProvider",
    );
  return ctx;
};
